/* eslint-disable no-undef */
/**
 * <wallpaper-gallery> — browses AI generations grouped by prompt.
 * Calls /apps/ai/gallery via Shopify App Proxy.
 */
(() => {
  if (customElements.get('wallpaper-gallery')) return;

  const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );

  class WallpaperGallery extends HTMLElement {
    constructor() {
      super();
      this.state = { loading: true, error: null, groups: [], query: '' };
    }

    connectedCallback() {
      this.proxy = this.dataset.proxyBase || '/apps/ai';
      this.studioUrl = this.dataset.studioUrl || '/pages/design-studio';
      this.limit = Number(this.dataset.limit) || 60;
      this.customerId = (this.dataset.customerId || '').trim();
      this.loginUrl = this.dataset.loginUrl || '/account/login';
      this.mine = (this.dataset.mine || '') === '1';
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('input', (e) => this.onInput(e));

      // Auth wall: if mine mode is on and customer isn't signed in, don't fetch.
      if (this.mine && !this.customerId) {
        this.state.loading = false;
        this.render();
        return;
      }
      this.fetchGroups();
    }

    async fetchGroups() {
      try {
        const params = new URLSearchParams({ limit: String(this.limit) });
        if (this.mine) params.set('mine', '1');
        const r = await fetch(`${this.proxy}/gallery?${params.toString()}`, {
          credentials: 'same-origin',
        });
        if (r.status === 401) {
          // Session expired between page load and fetch — treat as logged out.
          this.customerId = '';
          this.state.loading = false;
          this.render();
          return;
        }
        if (!r.ok) throw new Error('Failed to load gallery.');
        const { groups } = await r.json();
        this.state.groups = groups || [];
        this.state.loading = false;
        this.render();
      } catch (err) {
        this.state.error = err.message || 'Failed to load gallery.';
        this.state.loading = false;
        this.render();
      }
    }

    setState(patch) {
      Object.assign(this.state, patch);
      this.render();
    }

    filteredGroups() {
      const q = this.state.query.trim().toLowerCase();
      if (!q) return this.state.groups;
      return this.state.groups.filter((g) => (g.prompt || '').toLowerCase().includes(q));
    }

    render() {
      if (this.state.loading) {
        this.innerHTML = `
          <div class="wg-loading">
            <div class="ds-spinner"></div>
            <span>Loading the wallpaper library&hellip;</span>
          </div>`;
        return;
      }
      if (this.mine && !this.customerId) {
        this.innerHTML = `
          <div class="wg-empty wg-login-wall">
            <h2 class="wg-login-wall__title">Sign in to view your wallpapers</h2>
            <p>Your AI-generated designs are saved to your account. Log in to browse and remix them.</p>
            <p>
              <a class="ds-btn" href="${esc(this.loginUrl)}">Log in</a>
              <a class="ds-btn ds-btn--ghost" href="${esc(this.studioUrl)}">Create one first</a>
            </p>
          </div>`;
        return;
      }
      if (this.state.error) {
        this.innerHTML = `<p class="wg-error">${esc(this.state.error)}</p>`;
        return;
      }
      const groups = this.filteredGroups();
      const totalImages = groups.reduce((n, g) => n + g.count, 0);

      this.innerHTML = `
        <div class="wg-search">
          <input class="wg-search__input" type="search" placeholder="Search prompts (e.g. botanical, art deco)" value="${esc(this.state.query)}" data-role="search">
          <span class="wg-count">${groups.length} prompt${groups.length === 1 ? '' : 's'} &middot; ${totalImages} design${totalImages === 1 ? '' : 's'}</span>
        </div>
        ${groups.length === 0
          ? `<div class="wg-empty"><p>${this.state.query ? 'No prompts match your search.' : 'No wallpapers yet. Be the first to generate one!'}</p>
              <a class="ds-btn" href="${esc(this.studioUrl)}">Open Design Studio</a>
            </div>`
          : `<div class="wg-groups">
              ${groups.map((g) => this.renderGroup(g)).join('')}
            </div>`}`;
    }

    renderGroup(g) {
      const studioWithPrompt = `${this.studioUrl}?prompt=${encodeURIComponent(g.prompt)}`;
      return `
        <article class="wg-group">
          <header class="wg-group__head">
            <h2 class="wg-group__prompt">${esc(g.prompt)}</h2>
            <div class="wg-group__meta">
              <span class="wg-group__count">${g.count} design${g.count === 1 ? '' : 's'}</span>
              <a class="wg-remix" href="${esc(studioWithPrompt)}">Remix this prompt</a>
            </div>
          </header>
          <div class="wg-grid">
            ${g.images.map((url) => `
              <button class="wg-tile" data-action="view" data-url="${esc(url)}" data-prompt="${esc(g.prompt)}">
                <img src="${esc(url)}" alt="${esc(g.prompt)}" loading="lazy">
              </button>`).join('')}
          </div>
        </article>`;
    }

    onInput(e) {
      const el = e.target;
      if (el.dataset.role === 'search') {
        // debounce minimal — re-render on each keystroke is fine for client-side filter
        this.state.query = el.value;
        // Re-render but keep focus + caret
        const caret = el.selectionStart;
        this.render();
        const next = this.querySelector('[data-role="search"]');
        if (next) {
          next.focus();
          try { next.setSelectionRange(caret, caret); } catch {}
        }
      }
    }

    onClick(e) {
      const tile = e.target.closest('[data-action="view"]');
      if (tile) this.openLightbox(tile.dataset.url, tile.dataset.prompt);
    }

    openLightbox(url, prompt) {
      const studioWithPrompt = `${this.studioUrl}?prompt=${encodeURIComponent(prompt)}`;
      const box = document.createElement('div');
      box.className = 'wg-lightbox';
      box.innerHTML = `
        <div class="wg-lightbox__inner">
          <button class="wg-lightbox__close" aria-label="Close">&times;</button>
          <img src="${esc(url)}" alt="${esc(prompt)}">
          <p style="color:#fff;text-align:center;margin:0;font-size:.9rem;">${esc(prompt)}</p>
          <div class="wg-lightbox__bar">
            <a class="ds-btn" href="${esc(studioWithPrompt)}">Use this prompt</a>
            <a class="ds-btn ds-btn--ghost" href="${esc(url)}" download target="_blank" rel="noopener">Open original</a>
          </div>
        </div>`;
      const close = () => box.remove();
      box.addEventListener('click', (ev) => {
        if (ev.target === box || ev.target.classList.contains('wg-lightbox__close')) close();
      });
      document.addEventListener('keydown', function onKey(ev) {
        if (ev.key === 'Escape') { close(); document.removeEventListener('keydown', onKey); }
      });
      document.body.appendChild(box);
    }
  }

  customElements.define('wallpaper-gallery', WallpaperGallery);
})();
